import { useRef, useState, type CSSProperties } from 'react';

interface HighlightedCodesTextareaProps {
  value: string;
  onChange: (value: string) => void;
  unrecognizedLines: string[];
  placeholder?: string;
}

const sharedTextStyle: CSSProperties = {
  fontFamily: 'inherit',
  fontSize: 14,
  lineHeight: '22px',
  padding: '14px 16px',
  whiteSpace: 'pre-wrap',
  overflowWrap: 'break-word',
  boxSizing: 'border-box',
  margin: 0,
  border: 'none',
};

const HighlightedCodesTextarea = ({
  value,
  onChange,
  unrecognizedLines,
  placeholder,
}: HighlightedCodesTextareaProps) => {
  const backdropRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  const unrecognizedSet = new Set(unrecognizedLines);

  const syncScroll = () => {
    if (backdropRef.current && textareaRef.current) {
      backdropRef.current.scrollTop = textareaRef.current.scrollTop;
      backdropRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  };

  const lines = value.split(/\r\n|\r|\n/);

  return (
    <div
      className={`relative w-full h-[220px] rounded-lg border-2 overflow-hidden transition-colors
        ${isFocused ? 'border-[#ff7500]' : 'border-gray-200'}`}
    >
      <div
        ref={backdropRef}
        aria-hidden
        className='absolute inset-0 overflow-auto pointer-events-none text-gray-800'
        style={sharedTextStyle}
      >
        {value ? (
          lines.map((line, idx) => (
            <div key={idx}>
              {line ? (
                <span
                  style={
                    unrecognizedSet.has(line.trim())
                      ? {
                          textDecoration: 'underline',
                          textDecorationColor: '#d32f2f',
                          textDecorationThickness: 2,
                          textUnderlineOffset: 3,
                        }
                      : undefined
                  }
                >
                  {line}
                </span>
              ) : (
                ' '
              )}
            </div>
          ))
        ) : (
          <span className='text-gray-400'>{placeholder}</span>
        )}
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        onScroll={syncScroll}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        placeholder={placeholder}
        spellCheck={false}
        className='absolute inset-0 w-full h-full bg-transparent resize-none outline-none text-transparent overflow-auto'
        style={{ ...sharedTextStyle, caretColor: '#111827' }}
      />
    </div>
  );
};

export default HighlightedCodesTextarea;
